import axios from 'axios';
import auth from './authSvc';
import alertSvc from '@/services/alertSvc.js';

export const HTTP = axios.create({
  baseURL: auth.getApiUrl(),
  headers: { Pragma: 'no-cache' }
});

export const reauthFn = _reauth;

const REAUTH_URL = 'public/reauth/web';
const X_SESSION_TOKEN = 'x-session-token';
const X_REAUTH_TOKEN = 'x-reauth-token';
const INVALID_TOKEN_STATUS_TEXT = 'Invalid token';
const INVALID_SESSION_TEXT = 'Invalid session';
const NOT_LOGGED_IN = 'Not logged in';

let currentReauth;
let reauthInProgress;
/**
 * This adds an interceptor that automatically adds the session token to all requests
 */
HTTP.interceptors.request.use(function(config) {
  if (!auth.isLoggedIn()) {
    const reauthToken = auth.getReauthToken();
    const apiUrl = arguments[0].url;
    if (reauthToken && !_isReauthRequest(apiUrl)) {
      return _reauth(reauthToken).then(() => {
        config.headers[X_SESSION_TOKEN] = auth.getToken();
        return config;
      });
    } else if (!_isReauthRequest(apiUrl)) {
      // we have no reauth token so go to login
      auth.returnToLogin();
      return Promise.reject(new Error(NOT_LOGGED_IN));
    }
  } else {
    // we are not in a reauth state and do have a token so add it and proceed
    config.headers[X_SESSION_TOKEN] = auth.getToken();
  }
  return config;
});

/**
 * Executes the async reauth promise, setting extended session tokens if successful.
 * @param {string} reAuthToken
 */
function _reauth(reAuthToken) {
  var reauthView = {
    reauthToken: reAuthToken
  };
  if (!reauthInProgress) {
    reauthInProgress = true;
    try {
      currentReauth = HTTP.post(REAUTH_URL, reauthView).then((response) => {
        let token = response.headers[X_SESSION_TOKEN];
        let reAuthToken = response.headers[X_REAUTH_TOKEN];
        auth.clearToken();
        if (token) {
          auth.setToken(token);
        }
        if (reAuthToken) {
          auth.setReauthToken(reAuthToken);
        }
        reauthInProgress = false;
      });
    } catch (e) {
      // clear the tokens on reauth failure and bail to login
      auth.clearToken();
      auth.returnToLogin();
      return Promise.reject(new Error(NOT_LOGGED_IN));
    }
  }
  return currentReauth;
}
/**
 * Checks if the incoming URL is a reauth URL
 * @param {string} apiUrl
 */
function _isReauthRequest(apiUrl) {
  return apiUrl.endsWith(REAUTH_URL);
}

/**
 * This saves any session tokens returned by the server. If there was an auth issue, it will send the user to the login page
 */
HTTP.interceptors.response.use(function(response) {
  // noop on response now - only set the auth tokens on reauth calls
  return response;
}, function(error) {
  // Handle Invalid token - re-auth
  const errorResponse = error.response;
  if (
    errorResponse && !_isReauthRequest(arguments[0].config.url) &&
    (errorResponse.status === 400 || errorResponse.status === 401) &&
    (errorResponse.statusText === INVALID_TOKEN_STATUS_TEXT ||
      errorResponse.data.message === INVALID_TOKEN_STATUS_TEXT ||
      errorResponse.statusText === INVALID_SESSION_TEXT ||
      errorResponse.data.message === INVALID_SESSION_TEXT)
  ) {
    return _reauth(auth.getReauthToken()).then(
      // replay request after reauthenticating
      () => {
        error.config.headers['x-session-token'] = auth.getToken();
        return HTTP.request(error.config);
      },
      () => {
        // clear the tokens on reauth failure and bail to login
        auth.clearToken();
        auth.returnToLogin();
        return Promise.reject(new Error(NOT_LOGGED_IN));
      }
    );
  }
  if (error.response && error.response.status === 422) {
    // Do not display standard network issues alert for status code 422
    // instead let frontend handle unprocessable entity
    return Promise.reject(error);
  } else {
    alertSvc.networkError(error);
    return Promise.reject(error);
  }
});

export default HTTP;
